import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import type { RedoFulfillmentLocation } from "@redotech/redo-model/fulfillments/fulfillment-location";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useState } from "react";

function formatAddress(location: RedoFulfillmentLocation): string {
  const parts = [
    location.address.address1,
    location.address.city,
    location.address.provinceCode ?? location.address.province,
    location.address.zip,
  ].filter(Boolean);
  return parts.join(", ");
}

export const WarehouseLocationPickerModal = memo(
  function WarehouseLocationPickerModal({
    onClose,
    onSelect,
  }: {
    onClose(): void;
    onSelect(locationId: string): Promise<void>;
  }) {
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

    const locationsLoad = useLoad(async () => {
      const { locations } = await rpcClient.getLocations(null);
      return locations.filter((l) => l.isActive);
    }, [rpcClient]);

    const [selectedId, setSelectedId] = useState<string | null>(null);

    const [submitState, triggerSubmit] = useTriggerLoad(async () => {
      if (selectedId) {
        await onSelect(selectedId);
      }
    });

    const handleSubmit = useCallback(() => {
      triggerSubmit();
    }, [triggerSubmit]);

    const locations = locationsLoad.value;

    const options =
      locations?.map((loc) => ({
        id: loc.externalId,
        label: loc.name,
        description: formatAddress(loc),
      })) ?? [];

    return (
      <RedoModal
        modalSize="sm"
        onModalCloseRequested={onClose}
        primaryButton={{
          text: "Continue to checkout",
          onClick: handleSubmit,
          disabled: !selectedId,
          pending: submitState.pending,
          size: "sm",
        }}
        secondaryButton={{ text: "Cancel", onClick: onClose, size: "sm" }}
        title="Select shipping warehouse"
      >
        <Flex dir="column" gap="lg">
          <Text fontSize="sm" textColor="secondary">
            Choose which warehouse location this order should be shipped to.
          </Text>
          {locationsLoad.pending ? (
            <Text fontSize="sm" textColor="tertiary">
              Loading locations...
            </Text>
          ) : options.length === 0 ? (
            <Text fontSize="sm" textColor="tertiary">
              No active locations found.
            </Text>
          ) : (
            <RedoRadioGroup
              options={options}
              setValue={(value) => setSelectedId(value)}
              value={selectedId ?? ""}
            />
          )}
        </Flex>
      </RedoModal>
    );
  },
);
